import { supabase } from '@/shared/lib/supabase';
import { daysUntil } from '@/shared/utils/sastDate';

/**
 * Phase 11 sales-portal service layer.
 *
 * Queries: read from `customer_profile` / `lead_note` / `staff_profile` /
 * `sales_company` directly through RLS. The sales-scoped policies shipped in
 * Slice 1 restrict rows to caller's company + assigned leads.
 *
 * Mutations: call the Slice 2 Edge Functions (lead-batch-import, lead-convert,
 * etc.) which carry idempotency + audit logging.
 */

export type SalesStage = 'lead' | 'quoted' | 'customer' | 'lost';
export type LeadSource =
  | 'csv_import' | 'web_form' | 'referral' | 'cold_list' | 'partner' | 'walk_in' | 'inbound_call';

export interface LeadRow {
  id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  company: string | null;
  sales_stage: SalesStage;
  originating_agent_id: string | null;
  originating_company_id: string | null;
  assigned_agent_id: string | null;
  assigned_at: string | null;
  lead_source: LeadSource | null;
  lost_reason: string | null;
  lost_at: string | null;
  created_at: string;
}

export interface LeadNote {
  id: string;
  customer_profile_id: string;
  agent_id: string;
  note_text: string;
  created_at: string;
}

export interface SalesCompany {
  id: string;
  slug: string;
  name: string;
  billing_email: string;
  recurring_commission_pct: number;
  active: boolean;
  /** Which arithmetic applies. Under 'net_split', recurring_commission_pct is
   *  IGNORED by the engine — rendering it as the company's rate would state a
   *  share of gross when the deal is a share of net. */
  commission_model: CommissionModel;
  cost_per_account_cents: number;
  partner_split_pct: number;
  term_months: number | null;
}

export type AgentInviteStatus =
  | 'active'       // has signed in — fully onboarded
  | 'accepted'     // email confirmed, not yet signed in
  | 'invited'      // invite sent, awaiting acceptance
  | 'pending'      // profile exists, no invite recorded (invite email failed)
  | 'deactivated'; // offboarded

export interface StaffMember {
  id: string;
  display_name: string;
  email: string | null;
  phone: string | null;
  role: string;
  company_id: string;
  company_name: string | null;
  active: boolean;
  hired_at: string | null;
  invited_at: string | null;
  email_confirmed_at: string | null;
  last_sign_in_at: string | null;
  invite_status: AgentInviteStatus;
  /** Internal admin-console role (app_metadata): 'support' when the agent has
   *  scoped support access, otherwise null/'customer'. */
  app_role: string | null;
}

export interface UpdateAgentInput {
  display_name?: string;
  phone?: string | null;
  role?: 'sales_agent' | 'sales_manager';
  hired_at?: string | null;
  email?: string;
  resend_invite?: boolean;
  send_password_reset?: boolean;
}

export type CustomerHealthStatus =
  | 'active'
  | 'payment_pending'
  | 'overdue'
  | 'suspended'
  | 'terminated'
  | 'unknown';

/**
 * SALES-WIN-1: per-customer health-status row from `v_sales_customer_status`.
 * The view exposes only derived signals — balance is a single sum, last_paid_at
 * is a timestamp; no invoice content leaks (LD-S11 read-only envelope).
 */
export interface CustomerStatusRow {
  customer_id: string;
  full_name: string;
  phone: string | null;
  email: string | null;
  company: string | null;
  assigned_at: string | null;
  originating_company_id: string | null;
  originating_agent_id: string | null;
  assigned_agent_id: string | null;
  lifecycle_stage: string;
  sales_stage: string;
  health_status: CustomerHealthStatus;
  last_paid_at: string | null;
  outstanding_balance: number;
}

export interface AgentCommissionRow {
  customer_id: string;
  /** NULL for a partner-link sale (no agent) — the once-off is the Partner's
   *  (agreement 4.3A). Since 2026-09-25. */
  agent_id: string | null;
  agent_name: string | null;
  agent_email: string | null;
  company_id: string;
  company_name: string | null;
  company_slug: string | null;
  invoice_id: string;
  invoice_number: string | null;
  sale_date: string;
  /** SAST first-of-month `YYYY-MM-DD`. Build keys with `sastMonthKey()` — never
   *  `Date.UTC(...)`, which buckets a late-night month-end payment wrongly. */
  commission_month: string;
  target_kind: string;
  target_ref: string | null;
  description: string | null;
  commission_amount: number;
}

export interface CompanyCommissionRow {
  company_id: string;
  company_name: string;
  company_slug: string;
  customer_id: string;
  invoice_id: string;
  invoice_number: string | null;
  commission_date: string;
  /** SAST first-of-month `YYYY-MM-DD` — see AgentCommissionRow.commission_month. */
  commission_month: string;
  target_kind: string;
  target_ref: string | null;
  line_amount: number;
  recurring_commission_pct: number;
  commission_amount: number;
}

/** How a company is paid. See sales_company.commission_model. */
export type CommissionModel = 'flat_pct' | 'net_split';

/** Why a month paid out what it did — never leave a R0 unexplained. */
export type PayoutStatus = 'flat' | 'payable' | 'first_month_onceoff' | 'term_expired';

/**
 * One (company, customer, month) from v_sales_company_commission.
 * Amounts are rands, already rounded to 2dp by the view.
 * partner_amount + sighthost_amount always equals gross_amount.
 */
export interface CompanySplitCommissionRow {
  company_id: string;
  company_name: string;
  company_slug: string;
  commission_model: CommissionModel;
  customer_id: string;
  commission_month: string;
  first_month: string;
  term_month_index: number;
  term_months: number | null;
  originating_agent_id: string | null;
  cpanel_accounts: number;
  gross_amount: number;
  cost_amount: number;
  net_amount: number;
  partner_amount: number;
  sighthost_amount: number;
  payout_status: PayoutStatus;
}

export type PayoutMethod = 'eft' | 'payfast' | 'offset' | 'other';

/**
 * A settled partner month. Amounts are FROZEN at payment time, not recomputed —
 * a later refund must not restate a month that was already paid out.
 */
export interface CommissionPayout {
  id: string;
  company_id: string;
  payout_month: string;
  amount: number;
  gross_amount: number;
  cost_amount: number;
  customer_count: number;
  method: PayoutMethod;
  reference: string | null;
  notes: string | null;
  paid_at: string;
  recorded_by: string;
  voided_at: string | null;
  voided_by: string | null;
  void_reason: string | null;
}

/** Turn the RPC's error codes into something a person can act on. */
function payoutError(raw: string): string {
  if (raw.includes('not_authorised')) return 'Only SightHost oversight can record a partner payout.';
  if (raw.includes('already_paid')) return 'That month is already recorded as paid. Void the existing payout first.';
  if (raw.includes('nothing_to_pay')) return 'No commission was earned in that month.';
  if (raw.includes('zero_payout')) return 'That month earned R0, so there is nothing to settle.';
  if (raw.includes('reason_required')) return 'Give a reason — it stays on the record.';
  if (raw.includes('already_voided')) return 'That payout has already been voided.';
  return raw;
}

/** Commercial terms of a sales company, for the oversight drill-down. */
export interface SalesCompanyTerms {
  id: string;
  name: string;
  slug: string;
  active: boolean;
  commission_model: CommissionModel;
  recurring_commission_pct: number;
  cost_per_account_cents: number;
  partner_split_pct: number;
  term_months: number | null;
}

function uuid(): string {
  // Browser-safe UUID v4
  return crypto.randomUUID();
}

export interface ActiveServicePlan {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  recurring_amount: number;
  is_active: boolean;
}

export interface ActiveServiceSku {
  code: string;
  display_name: string;
  description: string | null;
  price_cents: number;
}

export interface SalesRegistrantInput {
  name: string;
  email: string;
  contactNumber: string;
  address1: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
  organisation?: string;
}

export interface CreateCustomerOrderInput {
  customer_email: string;
  customer_name: string;
  customer_phone?: string;
  plan_slug: string;
  fqdn_mode: 'register' | 'existing' | 'later';
  register_fqdn?: string;
  register_registrant?: SalesRegistrantInput;
  existing_fqdn?: string;
}

export interface CreateCustomerOrderResult {
  ok: true;
  customer_id: string;
  customer_created: boolean;
  invoice_id: string;
  invoice_number: string;
  pay_url: string;
  total_zar: number;
  emailed_to: string;
}

export type DomainAvailability =
  | { status: 'available'; price?: number }
  | { status: 'taken' }
  | { status: 'invalid' | 'tld_not_supported' | 'premium' | 'error'; message?: string };

/** ST-4 (2026-07-17) — sales quotation issuance via sales-service-order-create.
 *  SV: either sku_code (single) or items[] (multi one-off SKUs → bundle). */
export interface IssueQuotationInput {
  /** Existing lead/customer. Mutually exclusive with new_customer. */
  customer_profile_id?: string;
  /**
   * Brand-new prospect captured on the call — the EF creates the shadow
   * account and writes first-touch agent + company attribution.
   *
   * This input has existed in sales-service-order-create since 2026-07-17
   * (Slice ST) and NO UI had ever sent it: the Add Customer page could only
   * sell hosting, so Studio to a new customer had no path. Wired 2026-09-11.
   */
  new_customer?: { email: string; name: string; phone?: string };
  sku_code?: string;
  items?: string[];
  plan_months?: 3 | 6 | 12;
  deposit_amount_cents?: number;
  /** Studio bundle (G6): the free .co.za captured at quotation time. */
  fqdn_mode?: 'register' | 'existing' | 'later';
  register_fqdn?: string;
  register_registrant?: SalesRegistrantInput;
  existing_fqdn?: string;
  /**
   * W2 / GA-D1 (2026-08-10): agent discount %. Multiple of 0.5 in (0, 20];
   * the EF rejects >10 unless the caller is a sales_manager / admin. One-off
   * SKU + bundle quotations only (no hosting, no Studio payment plan).
   */
  discount_pct?: number;
  /**
   * Conversion (2026-07-29): invoice id of a LIVE quotation this one replaces.
   * The EF cancels it — and its service_order + domain order — only after the
   * replacement exists, and carries any chosen .co.za onto the new order.
   * Without this the customer holds two live pay links and can pay the wrong one.
   */
  supersede_quotation_id?: string;
}

export interface IssueQuotationResult {
  ok: true;
  invoice_id: string;
  quote_number: string;
  pay_url: string;
  total_zar: number;
  emailed_to: string;
  quote_expires_at: string;
  customer_created?: boolean;
  /** Present when this quotation replaced another. */
  superseded_quote_number?: string | null;
  /** Domain carried over from the superseded order, if any. */
  domain_carried_forward?: string | null;
}

/** The package + domain a lead/customer selected, from v_sales_lead_order
 *  (owner-run, scoped to the caller's leads). Null when the lead has no order. */
export interface LeadOrder {
  customer_id: string;
  service_order_id: string;
  plan_name: string | null;
  fqdn_mode: 'register' | 'existing' | 'later';
  domain_name: string | null;
  order_status: string;
  invoice_number: string | null;
  invoice_amount: number | null;
  payment_state: string | null;
  ordered_at: string;
  /** QM (2026-07-17): 'quotation' until the customer pays; then 'invoice'. */
  doc_type: 'quotation' | 'invoice' | null;
  /** Original QUO- number, present on converted (paid) orders. */
  quote_number: string | null;
  quote_expires_at: string | null;
  /** 2026-07-29: the view no longer filters to hosting, so callers must be able
   *  to tell a hosting order from studio-starter / care-pro / a one-off. */
  sku_code: string | null;
  /** Needed to name the exact quotation when superseding it during a conversion. */
  invoice_id: string | null;
}

/**
 * Human label for whatever the lead ordered.
 *
 * `plan_name` comes from `service_plan`, which exists for HOSTING ONLY. Once
 * v_sales_lead_order stopped filtering to hosting (2026-07-29), every Studio /
 * Care / one-off order started rendering as a bare "—" on the lead card, both
 * quotation lists and the CSV export — so an agent who converted a lead to
 * Studio saw the row change to a blank package at R500 and had no confirmation
 * the conversion had worked. That ambiguity is exactly what made agents report
 * the conversion flow as missing in the first place.
 *
 * Falls back to the sku, then to a title-cased slug, so a SKU added later still
 * renders something sane rather than a dash.
 */
const SKU_LABELS: Record<string, string> = {
  'hosting': 'Hosting',
  'studio-starter': 'Studio Starter',
  'care-pro': 'SightHost Care',
  'email-setup': 'Email Setup',
  'site-boost': 'Site Boost',
  'gbp-setup': 'Local Visibility (GBP)',
};

export function packageLabel(
  order: { plan_name?: string | null; sku_code?: string | null } | null | undefined
): string | null {
  if (!order) return null;
  if (order.plan_name) return order.plan_name;
  const sku = order.sku_code;
  if (!sku) return null;
  return (
    SKU_LABELS[sku] ??
    sku.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
  );
}

/** Payment-state → badge label + tone. Single source for the lead drill-down
 *  and the leads list, so they never drift. Tones are valid @afrilink/ui StatusTones. */
export type PaymentTone = 'paid' | 'pending' | 'overdue' | 'failed' | 'draft';
const PAYMENT_LABELS: Record<string, { label: string; tone: PaymentTone }> = {
  paid:            { label: 'Paid',             tone: 'paid' },
  pending_payment: { label: 'Awaiting payment', tone: 'pending' },
  unpaid:          { label: 'Unpaid',           tone: 'pending' },
  grace:           { label: 'Overdue',          tone: 'overdue' },
  suspended:       { label: 'Suspended',        tone: 'failed' },
  cancelled:       { label: 'Cancelled',        tone: 'draft' },
  refunded:        { label: 'Refunded',         tone: 'draft' },
  draft:           { label: 'Draft',            tone: 'draft' },
};
export function leadPaymentBadge(state: string | null | undefined): { label: string; tone: PaymentTone } | null {
  if (!state) return null;
  return PAYMENT_LABELS[state] ?? { label: state, tone: 'draft' };
}

/* ────────────────────────────────────────────────────────────────────────────
 * SM-R3 (2026-07-27) — expiring-quotations surface.
 *
 * Before this, `quote_expires_at` rendered on SalesLeadDetailPage ONLY. To find
 * out which quotations lapsed this week a manager had to open every lead page
 * one at a time — which is how QUO-2026-000001 came within days of
 * auto-cancelling with the customer's money already in the bank.
 *
 * Composed client-side from two owner-run, RLS-scoped reads that the sales
 * pages already make (v_sales_lead_order + customer_profile). No new view, no
 * new EF — AD-27 read-through holds.
 * Spec: _bmad-output/SALES_OPS_REVIEW_2026-07-27.md §B.3 SM-R3.
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * One LIVE quotation, straight off `v_sales_lead_quotations`.
 *
 * Distinct from `LeadOrder`, which comes from `v_sales_lead_order` — a
 * LATERAL … LIMIT 1 that returns only a lead's LATEST order. That view is why
 * a lead holding three live quotations showed one (2026-08-05,
 * info@lionroar.group); the other two stayed payable and invisible.
 */
export interface LeadQuotation {
  customer_id: string;
  invoice_id: string;
  quote_number: string;
  amount: number | null;
  issue_date: string | null;
  issued_at: string;
  quote_expires_at: string | null;
  payment_state: string | null;
  service_order_id: string | null;
  sku_code: string | null;
  order_status: string | null;
  fqdn_mode: 'register' | 'existing' | 'later' | null;
  /** >1 when a Slice-SV bundle put several service_orders on one quotation. */
  order_count: number;
  plan_name: string | null;
  domain_name: string | null;
}

/** Payload for sales-quotation-cancel. */
export interface CancelQuotationInput {
  invoice_id: string;
  reason?: string;
}

export interface CancelQuotationResult {
  ok: true;
  quote_number: string;
  service_orders_cancelled: number;
  domain_orders_cancelled: number;
}

/** A live quotation joined to the lead it belongs to. */
export interface QuotationRow {
  customer_id: string;
  /** Identifies the QUOTATION, not the lead — a lead may hold several. */
  invoice_id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  company: string | null;
  assigned_agent_id: string | null;
  sales_stage: SalesStage;
  /** For an unconverted quotation this IS the QUO- number. */
  quote_reference: string | null;
  amount: number | null;
  plan_name: string | null;
  /** Null for hosting (plan_name carries it); set for Studio / Care / one-offs. */
  sku_code: string | null;
  domain_name: string | null;
  ordered_at: string;
  quote_expires_at: string | null;
  payment_state: string | null;
  /** Whole days from today until expiry. Negative = already lapsed. */
  days_left: number | null;
}

/** Response of the sales-quotation-pay-link EF. */
export interface QuotationPayLink {
  pay_url: string;
  reference: string;
  amount: number;
  expires_at: string | null;
  customer_name: string | null;
  /** Caller's own staff_profile.display_name, so the message signs correctly. */
  sender_name: string | null;
}

export type QuotationUrgency = 'expired' | 'critical' | 'soon' | 'ok' | 'unknown';

/**
 * Urgency band from days remaining. Thresholds match the billing-cron nudge
 * cadence (T-7 / T-2) so the colour a manager sees agrees with the email the
 * customer just got.
 */
export function quotationUrgency(daysLeft: number | null): QuotationUrgency {
  if (daysLeft === null) return 'unknown';
  if (daysLeft < 0) return 'expired';
  if (daysLeft <= 2) return 'critical';
  if (daysLeft <= 7) return 'soon';
  return 'ok';
}

export const QUOTATION_URGENCY_TONE: Record<QuotationUrgency, PaymentTone> = {
  expired: 'draft',
  critical: 'failed',
  soon: 'overdue',
  ok: 'pending',
  unknown: 'draft',
};

// `daysUntil` now lives in @/shared/utils/sastDate — this copy counted from
// LOCAL midnight, so an agent whose laptop was on a non-SAST clock saw a
// different number of days left on the same quotation than the office did.

export const salesService = {
  /** All lead orders in the caller's scope, keyed by customer_id — for the
   *  leads list (package + domain columns). One owner-run query, not per-row. */
  async listLeadOrders(): Promise<Map<string, LeadOrder>> {
    const { data, error } = await supabase.from('v_sales_lead_order').select('*');
    if (error || !data) return new Map();
    return new Map((data as LeadOrder[]).map((o) => [o.customer_id, o]));
  },

  /**
   * SM-R3 — every LIVE quotation in the caller's scope, soonest expiry first.
   *
   * "Live" = the document is still a quotation (`doc_type='quotation'`) and has
   * not been settled or cancelled. A quotation that converts becomes
   * doc_type='invoice' via the in-place convert trigger, so it drops out of
   * this list automatically — no separate "converted" flag to keep in step.
   *
   * Lapsed-but-not-yet-cancelled rows are deliberately INCLUDED: billing-cron
   * STEP 0 cancels them on its next pass, and until it does they are still
   * recoverable by a phone call. Hiding them would hide the recoverable window.
   */
  async listQuotations(): Promise<QuotationRow[]> {
    // 2026-08-05: reads v_sales_lead_quotations (ALL live quotations) rather
    // than v_sales_lead_order (the lead's LATEST order only). Under the old
    // source a lead with three live quotations contributed one row, so the
    // other two were unreachable from this screen — and the "at stake" total
    // under-counted the real exposure.
    const [quotes, leads] = await Promise.all([this.listAllLiveQuotations(), this.listLeads()]);
    const leadById = new Map(leads.map((l) => [l.id, l]));

    const rows: QuotationRow[] = [];
    for (const q of quotes) {
      const lead = leadById.get(q.customer_id);
      if (!lead) continue; // converted/lost leads fall outside listLeads' stage filter
      rows.push({
        customer_id: q.customer_id,
        invoice_id: q.invoice_id,
        full_name: lead.full_name,
        email: lead.email,
        phone: lead.phone,
        company: lead.company,
        assigned_agent_id: lead.assigned_agent_id,
        sales_stage: lead.sales_stage,
        quote_reference: q.quote_number,
        amount: q.amount,
        plan_name: q.plan_name,
        sku_code: q.sku_code,
        domain_name: q.fqdn_mode === 'later' ? null : q.domain_name,
        ordered_at: q.issued_at,
        quote_expires_at: q.quote_expires_at,
        payment_state: q.payment_state,
        days_left: daysUntil(q.quote_expires_at),
      });
    }

    // Soonest expiry first; nulls last so undated rows never mask urgent ones.
    return rows.sort((a, b) => {
      if (a.days_left === null) return 1;
      if (b.days_left === null) return -1;
      return a.days_left - b.days_left;
    });
  },

  /**
   * Every LIVE quotation in the caller's scope — one row per quotation.
   *
   * Owner-run + RLS-scoped in the view, so this is a plain read (AD-27).
   * Soonest expiry first so the caller can render an urgency list directly.
   */
  async listAllLiveQuotations(): Promise<LeadQuotation[]> {
    const { data, error } = await supabase
      .from('v_sales_lead_quotations')
      .select('*')
      .order('quote_expires_at', { ascending: true, nullsFirst: false });
    if (error) throw new Error(error.message);
    return (data as LeadQuotation[]) ?? [];
  },

  /** Every LIVE quotation for ONE lead, oldest first (so the drill-down reads
   *  as a history). Non-fatal: an error just yields an empty list. */
  async listLeadQuotations(customerId: string): Promise<LeadQuotation[]> {
    const { data, error } = await supabase
      .from('v_sales_lead_quotations')
      .select('*')
      .eq('customer_id', customerId)
      .order('issued_at', { ascending: true });
    if (error) return [];
    return (data as LeadQuotation[]) ?? [];
  },

  /**
   * Withdraw a live quotation. Cancels the invoice AND its service /
   * domain-registration orders, server-side, in the safe order.
   *
   * Withdrawal is a state change, never a delete: a QUO- number comes from
   * `next_quote_number()` and deleting the row would hole the sequence and lose
   * the record of what was offered (CLAUDE.md §4.10 discipline).
   */
  async cancelQuotation(input: CancelQuotationInput): Promise<CancelQuotationResult> {
    const { data, error } = await supabase.functions.invoke('sales-quotation-cancel', {
      headers: { 'Idempotency-Key': uuid() },
      body: input,
    });
    if (error) throw new Error(await extractError(error));
    const res = data as CancelQuotationResult | null;
    if (!res?.ok) throw new Error('Quotation could not be withdrawn.');
    return res;
  },

  /** The hosting order (package + domain + payment state) a lead selected.
   *  Reads the owner-run v_sales_lead_order view; returns null if none. */
  async getLeadOrder(customerId: string): Promise<LeadOrder | null> {
    const { data, error } = await supabase
      .from('v_sales_lead_order')
      .select('*')
      .eq('customer_id', customerId)
      .maybeSingle();
    if (error) return null; // non-fatal — the drill-down just omits the card
    return (data as LeadOrder | null) ?? null;
  },

  /** Active hosting plans for the Add-Customer funnel. */
  async listActivePlans(): Promise<ActiveServicePlan[]> {
    const { data, error } = await supabase
      .from('service_plan')
      .select('id, slug, name, description, recurring_amount, display_order, is_active')
      .eq('is_active', true)
      .order('display_order', { ascending: true });
    if (error) throw new Error(error.message);
    return (data ?? []) as ActiveServicePlan[];
  },

  /**
   * Active service SKUs for the Add-Customer catalogue.
   *
   * Read straight from the DB (RLS: service_sku_anon_read, active = true) so
   * the description an agent reads on a live call is the same text the
   * catalogue holds — a hardcoded label list in the portal drifts the moment
   * a price or an inclusion changes.
   */
  async listActiveSkus(): Promise<ActiveServiceSku[]> {
    const { data, error } = await supabase
      .from('service_sku')
      .select('code, display_name, description, price_cents')
      .eq('active', true);
    if (error) throw new Error(error.message);
    return (data ?? []) as ActiveServiceSku[];
  },

  /** Public domain-availability check (same EF the customer funnel uses).
   *  EF response: { available, fqdn, sld, tld, isPremium, usesEppKey, ... }.
   *  Unsupported TLDs / malformed input come back as a 4xx (→ error branch). */
  async checkDomain(fqdn: string): Promise<DomainAvailability> {
    const { data, error } = await supabase.functions.invoke<{
      available?: boolean;
      isPremium?: boolean;
    }>('domain-availability', { body: { domain: fqdn } });
    if (error) {
      let code = '';
      try {
        const ctx = (error as { context?: { response?: Response } }).context;
        if (ctx?.response) code = (await ctx.response.clone().json())?.code ?? '';
      } catch { /* ignore */ }
      if (code === 'tld_not_supported') return { status: 'tld_not_supported' };
      if (code === 'invalid_domain' || code === 'invalid') return { status: 'invalid' };
      return { status: 'error', message: error.message };
    }
    if (data?.isPremium) return { status: 'premium' };
    if (data?.available) return { status: 'available' };
    return { status: 'taken' };
  },

  /**
   * Add a customer directly and email them a PayFast pay-link (pay-first).
   * Creates a shadow account (no password) + hosting order (pending_payment).
   * The customer sets a password automatically only AFTER paying.
   */
  async createCustomerOrder(input: CreateCustomerOrderInput): Promise<CreateCustomerOrderResult> {
    const { data, error } = await supabase.functions.invoke<CreateCustomerOrderResult>('sales-order-create', {
      headers: { 'Idempotency-Key': uuid() },
      body: input,
    });
    if (error) {
      // Surface the EF's structured error code/message when present.
      let msg = error.message;
      try {
        const ctx = (error as { context?: { response?: Response } }).context;
        if (ctx?.response) {
          const b = await ctx.response.clone().json();
          if (b?.message) msg = b.message;
          else if (b?.code) msg = b.code;
        }
      } catch { /* ignore */ }
      throw new Error(msg);
    }
    if (!data?.ok) throw new Error('Order creation failed.');
    return data;
  },

  /** List leads with optional filters. Reads through RLS — scope-limited per caller. */
  async listLeads(opts: { stage?: SalesStage; assignedAgentId?: string } = {}): Promise<LeadRow[]> {
    let q = supabase
      .from('customer_profile')
      .select('id, full_name, billing_email, phone, company, sales_stage, originating_agent_id, originating_company_id, assigned_agent_id, assigned_at, lead_source, lost_reason, lost_at, created_at')
      .in('sales_stage', ['lead', 'quoted'])
      .order('created_at', { ascending: false });
    if (opts.stage) q = q.eq('sales_stage', opts.stage);
    if (opts.assignedAgentId) q = q.eq('assigned_agent_id', opts.assignedAgentId);
    const { data, error } = await q;
    if (error) throw new Error(error.message);
    return (data ?? []).map((r) => ({
      ...r,
      email: (r as { billing_email?: string }).billing_email ?? null,
    })) as LeadRow[];
  },

  /** Single lead with notes thread + assigned agent name. */
  async getLead(id: string): Promise<{ lead: LeadRow | null; notes: LeadNote[] }> {
    const [leadResult, notesResult] = await Promise.all([
      supabase
        .from('customer_profile')
        .select('id, full_name, billing_email, phone, company, sales_stage, originating_agent_id, originating_company_id, assigned_agent_id, assigned_at, lead_source, lost_reason, lost_at, created_at')
        .eq('id', id)
        .maybeSingle(),
      supabase
        .from('lead_note')
        .select('id, customer_profile_id, agent_id, note_text, created_at')
        .eq('customer_profile_id', id)
        .order('created_at', { ascending: false }),
    ]);
    if (leadResult.error) throw new Error(leadResult.error.message);
    if (notesResult.error) throw new Error(notesResult.error.message);
    const lead = leadResult.data
      ? ({ ...leadResult.data, email: (leadResult.data as { billing_email?: string }).billing_email ?? null } as LeadRow)
      : null;
    return { lead, notes: (notesResult.data ?? []) as LeadNote[] };
  },

  /**
   * SALES-WIN-1: converted customers with derived health status from
   * `v_sales_customer_status`. View runs as owner; rows scoped by embedded
   * admin/sales-staff check; LD-S11 envelope holds (no raw invoice content).
   */
  async listCustomerStatus(): Promise<CustomerStatusRow[]> {
    const { data, error } = await supabase
      .from('v_sales_customer_status')
      .select('customer_id, full_name, phone, email, company, assigned_at, originating_company_id, originating_agent_id, assigned_agent_id, lifecycle_stage, sales_stage, health_status, last_paid_at, outstanding_balance')
      .order('assigned_at', { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []) as CustomerStatusRow[];
  },

  /** Converted customers (ATA-3 filter). */
  async listConvertedCustomers(): Promise<LeadRow[]> {
    const { data, error } = await supabase
      .from('customer_profile')
      .select('id, full_name, billing_email, phone, company, sales_stage, originating_agent_id, originating_company_id, assigned_agent_id, assigned_at, lead_source, lost_reason, lost_at, created_at')
      .in('sales_stage', ['customer', 'lost'])
      .not('originating_company_id', 'is', null)
      .order('created_at', { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []).map((r) => ({
      ...r,
      email: (r as { billing_email?: string }).billing_email ?? null,
    })) as LeadRow[];
  },

  /** List agents (manager+ for own company, admin for all). Reads the
   *  owner-run v_sales_agent view so invite-acceptance status + email are
   *  visible; the view enforces company/admin scope in its own WHERE. */
  async listAgents(companyId?: string): Promise<StaffMember[]> {
    let q = supabase
      .from('v_sales_agent')
      .select('id, display_name, email, phone, role, company_id, company_name, active, hired_at, invited_at, email_confirmed_at, last_sign_in_at, invite_status, app_role')
      .order('display_name');
    if (companyId) q = q.eq('company_id', companyId);
    const { data, error } = await q;
    if (error) throw new Error(error.message);
    return (data ?? []) as StaffMember[];
  },

  /** Single agent by user_id (same view + scope as listAgents). */
  async getAgent(userId: string): Promise<StaffMember | null> {
    const { data, error } = await supabase
      .from('v_sales_agent')
      .select('id, display_name, email, phone, role, company_id, company_name, active, hired_at, invited_at, email_confirmed_at, last_sign_in_at, invite_status, app_role')
      .eq('id', userId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return (data ?? null) as StaffMember | null;
  },

  /** ST-4 (2026-07-17): issue a QUOTATION for any active SKU to an existing
   *  lead. Studio Starter carries the agent-chosen deposit (floor R500) +
   *  optional 3/6/12-month plan. Routes through sales-service-order-create;
   *  paying the quotation converts it in place and fires the services saga. */
  async issueQuotation(input: IssueQuotationInput): Promise<IssueQuotationResult> {
    const { data, error } = await supabase.functions.invoke('sales-service-order-create', {
      headers: { 'Idempotency-Key': uuid() },
      body: input,
    });
    if (error) throw new Error(await extractError(error));
    const res = data as IssueQuotationResult | null;
    if (!res?.ok) throw new Error('Quotation could not be issued.');
    return res;
  },

  /** Amend an agent's details and/or (re)send their invite. Manager (own
   *  company) or admin. Routes through the staff-update EF for audit + auth. */
  async updateAgent(userId: string, patch: UpdateAgentInput): Promise<{ email_changed: boolean; invite_sent: boolean }> {
    const { data, error } = await supabase.functions.invoke('staff-update', {
      headers: { 'Idempotency-Key': uuid() },
      body: { user_id: userId, ...patch },
    });
    if (error) throw new Error(await extractError(error));
    return data as { email_changed: boolean; invite_sent: boolean };
  },

  /** Re-send the Supabase invite email as-is. Convenience over updateAgent. */
  async resendAgentInvite(userId: string): Promise<{ invite_sent: boolean }> {
    const { data, error } = await supabase.functions.invoke('staff-update', {
      headers: { 'Idempotency-Key': uuid() },
      body: { user_id: userId, resend_invite: true },
    });
    if (error) throw new Error(await extractError(error));
    return data as { invite_sent: boolean };
  },

  /** Email the agent a one-shot set-password link (branded password_reset
   *  template, token_hash form). Works in every state — including an agent
   *  whose invite link was clicked but never completed, which GoTrue then
   *  refuses to re-invite. Manager (own company) or admin; audited as the
   *  caller. A second send invalidates the first link. */
  async sendAgentPasswordReset(userId: string): Promise<{ reset_sent: boolean }> {
    const { data, error } = await supabase.functions.invoke('staff-update', {
      headers: { 'Idempotency-Key': uuid() },
      body: { user_id: userId, send_password_reset: true },
    });
    if (error) throw new Error(await extractError(error));
    return data as { reset_sent: boolean };
  },

  /** Grant/revoke the scoped internal `support` role (app_metadata). Admin-only
   *  (staff-role-set enforces it). Enabling gives the agent the support console
   *  (client operations, no confidential financials); disabling sets role back
   *  to 'customer' — their sales_agent staff_profile is untouched either way. */
  async setSupportAccess(userId: string, enabled: boolean): Promise<void> {
    const { error } = await supabase.functions.invoke('staff-role-set', {
      body: { user_id: userId, role: enabled ? 'support' : 'customer' },
    });
    if (error) throw new Error(await extractError(error));
  },

  /** Agent commission view (RLS-scoped — agent sees own; manager sees company; admin sees all). */
  async listAgentCommission(monthIso?: string): Promise<AgentCommissionRow[]> {
    let q = supabase
      .from('v_sales_agent_commission')
      .select('customer_id, agent_id, agent_name, agent_email, company_id, company_name, company_slug, invoice_id, invoice_number, sale_date, commission_month, target_kind, target_ref, description, commission_amount')
      .order('sale_date', { ascending: false });
    if (monthIso) q = q.eq('commission_month', monthIso);
    const { data, error } = await q;
    if (error) throw new Error(error.message);
    return (data ?? []) as AgentCommissionRow[];
  },

  /** Company recurring commission view (admin sees all; manager/agent scoped by their company). */
  async listCompanyCommission(monthIso?: string): Promise<CompanyCommissionRow[]> {
    let q = supabase
      .from('v_sales_company_recurring_commission')
      .select('company_id, company_name, company_slug, customer_id, invoice_id, invoice_number, commission_date, commission_month, target_kind, target_ref, line_amount, recurring_commission_pct, commission_amount')
      .order('commission_date', { ascending: false });
    if (monthIso) q = q.eq('commission_month', monthIso);
    const { data, error } = await q;
    if (error) throw new Error(error.message);
    return (data ?? []) as CompanyCommissionRow[];
  },

  /**
   * Company commission from the split engine (v_sales_company_commission).
   *
   * Grain is (company, customer, MONTH), not per line, because the net_split
   * cost basis is per cPanel account per month — see the view comment. Handles
   * both commission models; for flat_pct companies partner_amount equals what
   * the old line-grain view reported, aggregated.
   *
   * Scoped by the view: full staff and house sales managers see every company,
   * a partner sales_manager sees only their own, agents see nothing (LD-S1).
   */
  async listCompanySplitCommission(opts: { companyId?: string; monthIso?: string } = {}) {
    let q = supabase
      .from('v_sales_company_commission')
      .select('company_id, company_name, company_slug, commission_model, customer_id, commission_month, first_month, term_month_index, term_months, originating_agent_id, cpanel_accounts, gross_amount, cost_amount, net_amount, partner_amount, sighthost_amount, payout_status')
      .order('commission_month', { ascending: false });
    if (opts.companyId) q = q.eq('company_id', opts.companyId);
    if (opts.monthIso) q = q.eq('commission_month', opts.monthIso);
    const { data, error } = await q;
    if (error) throw new Error(error.message);
    return (data ?? []) as CompanySplitCommissionRow[];
  },

  /** One sales company's commercial terms, for the oversight drill-down. */
  async getCompany(companyId: string) {
    const { data, error } = await supabase
      .from('sales_company')
      .select('id, name, slug, active, commission_model, recurring_commission_pct, cost_per_account_cents, partner_split_pct, term_months')
      .eq('id', companyId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data as SalesCompanyTerms | null;
  },

  /** Is the signed-in user SightHost oversight (may record partner payouts)? */
  async isHouseOversight(): Promise<boolean> {
    const { data, error } = await supabase.rpc('my_sales_oversight');
    if (error) return false;
    return data === true;
  },

  /** Payout history for a company. A partner sees their own; oversight sees all. */
  async listPayouts(companyId: string): Promise<CommissionPayout[]> {
    const { data, error } = await supabase
      .from('sales_commission_payout')
      .select('id, company_id, payout_month, amount, gross_amount, cost_amount, customer_count, method, reference, notes, paid_at, recorded_by, voided_at, voided_by, void_reason')
      .eq('company_id', companyId)
      .order('payout_month', { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []) as CommissionPayout[];
  },

  /**
   * Record a month as settled. The AMOUNT IS NOT PASSED — the RPC derives it
   * from the commission engine, so the ledger cannot record a figure that was
   * never owed.
   */
  async recordPayout(input: {
    companyId: string;
    month: string;
    method: PayoutMethod;
    reference?: string;
    notes?: string;
  }): Promise<string> {
    const { data, error } = await supabase.rpc('sales_commission_payout_record', {
      p_company_id: input.companyId,
      p_month: input.month,
      p_method: input.method,
      p_reference: input.reference ?? null,
      p_notes: input.notes ?? null,
    });
    if (error) throw new Error(payoutError(error.message));
    return data as string;
  },

  /** Void a payout recorded in error. Never edits the amount — see the table comment. */
  async voidPayout(payoutId: string, reason: string): Promise<void> {
    const { error } = await supabase.rpc('sales_commission_payout_void', {
      p_payout_id: payoutId,
      p_reason: reason,
    });
    if (error) throw new Error(payoutError(error.message));
  },

  /** Resolve agent display_name by id — used in lead lists for cleaner labels. */
  async resolveAgentNames(agentIds: string[]): Promise<Record<string, string>> {
    if (agentIds.length === 0) return {};
    const unique = Array.from(new Set(agentIds.filter(Boolean)));
    const { data, error } = await supabase
      .from('staff_profile')
      .select('id, display_name')
      .in('id', unique);
    if (error) return {};
    return Object.fromEntries((data ?? []).map((r) => [r.id, r.display_name as string]));
  },

  /** List sales companies. RLS: admin sees all; sales staff see their own. */
  async listCompanies(): Promise<SalesCompany[]> {
    const { data, error } = await supabase
      .from('sales_company')
      .select('id, slug, name, billing_email, recurring_commission_pct, active, commission_model, cost_per_account_cents, partner_split_pct, term_months')
      .order('name');
    if (error) throw new Error(error.message);
    return (data ?? []) as SalesCompany[];
  },

  /** Create a new sales_company. Admin-only via RLS `sales_company_write_admin`. */
  async createCompany(input: {
    name: string;
    slug: string;
    billing_email: string;
    recurring_commission_pct?: number;
    active?: boolean;
  }): Promise<SalesCompany> {
    const { data, error } = await supabase
      .from('sales_company')
      .insert({
        name: input.name,
        slug: input.slug,
        billing_email: input.billing_email,
        recurring_commission_pct: input.recurring_commission_pct ?? 12.5,
        active: input.active ?? true,
      })
      .select('id, slug, name, billing_email, recurring_commission_pct, active, commission_model, cost_per_account_cents, partner_split_pct, term_months')
      .single();
    if (error) throw new Error(error.message);
    return data as SalesCompany;
  },

  /**
   * Update a sales_company row. Admin-only. Routes via `sales-company-update` EF
   * (not direct SQL) so every change is audit-logged with before/after diff.
   * SALES-RATE-2 — replaces the v1.1 banner workflow.
   */
  async updateCompany(companyId: string, updates: {
    name?: string;
    billing_email?: string;
    recurring_commission_pct?: number;
    active?: boolean;
  }): Promise<SalesCompany> {
    const { data, error } = await supabase.functions.invoke('sales-company-update', {
      headers: { 'Idempotency-Key': uuid() },
      body: { company_id: companyId, ...updates },
    });
    if (error) throw new Error(await extractError(error));
    return (data as { company: SalesCompany }).company;
  },

  // ===== Mutation EF invocations =====

  async addNote(customerProfileId: string, noteText: string): Promise<LeadNote> {
    const { data, error } = await supabase.functions.invoke('lead-add-note', {
      headers: { 'Idempotency-Key': uuid() },
      body: { customer_profile_id: customerProfileId, note_text: noteText },
    });
    if (error) throw new Error(await extractError(error));
    return (data as { note: LeadNote }).note;
  },

  async markLost(customerProfileId: string, lostReason: string): Promise<void> {
    const { error } = await supabase.functions.invoke('lead-mark-lost', {
      headers: { 'Idempotency-Key': uuid() },
      body: { customer_profile_id: customerProfileId, lost_reason: lostReason },
    });
    if (error) throw new Error(await extractError(error));
  },

  async convertLead(customerProfileId: string): Promise<{ redirect_url: string; magic_link_sent: boolean }> {
    const { data, error } = await supabase.functions.invoke('lead-convert', {
      headers: { 'Idempotency-Key': uuid() },
      body: { customer_profile_id: customerProfileId },
    });
    if (error) throw new Error(await extractError(error));
    return data as { redirect_url: string; magic_link_sent: boolean };
  },

  /**
   * Resend the invoice (pay-link) email for one of the agent's own leads,
   * while it is still pre-conversion + unpaid (Option C, 2026-07-09). The
   * sales-invoice-resend EF enforces attribution + the unpaid gate; once the
   * customer has paid (converted), it returns invoice_already_paid.
   */
  async resendLeadInvoice(customerProfileId: string): Promise<{ to_email: string }> {
    const { data, error } = await supabase.functions.invoke('sales-invoice-resend', {
      headers: { 'Idempotency-Key': uuid() },
      body: { customer_profile_id: customerProfileId },
    });
    if (error) throw new Error(await extractError(error));
    return data as { to_email: string };
  },

  /**
   * Raise a manual-payment (EFT) capture request for one of the caller's own
   * pre-conversion leads. The agent NEVER records the payment — this creates a
   * pending proof_of_payment row that support verifies (LD-S11 / SALES-ISO-1).
   * Attest-only (build decision 1-A): reference/date/note, no file. Once the
   * customer converts (sales_stage='customer') the EF returns invoice_already_paid.
   */
  async requestPaymentCapture(
    customerProfileId: string,
    opts?: { reference?: string; paid_on?: string; agent_note?: string },
  ): Promise<{ proof_id: string; invoice_number: string }> {
    const { data, error } = await supabase.functions.invoke('sales-payment-capture-request', {
      headers: { 'Idempotency-Key': uuid() },
      body: {
        customer_profile_id: customerProfileId,
        reference: opts?.reference,
        paid_on: opts?.paid_on,
        agent_note: opts?.agent_note,
      },
    });
    if (error) throw new Error(await extractError(error));
    return data as { proof_id: string; invoice_number: string };
  },

  /**
   * Mint a copy-paste payment link for one of the caller's own leads.
   *
   * QUOTATIONS ONLY for the sales tier (owner decision 2026-07-27) — the EF
   * returns 409 `not_a_quotation` for a converted customer's invoice. Active
   * customers are serviced by admin/support on the /admin surface, so do NOT
   * "fix" that 409 by widening the EF; it is the control, not a bug.
   *
   * No Idempotency-Key: this mutates nothing (payment_token already exists and
   * is stable) and its only write is an append-only disclosure audit row, which
   * we want one of per issuance.
   */
  async getQuotationPayLink(customerProfileId: string): Promise<QuotationPayLink> {
    const { data, error } = await supabase.functions.invoke('sales-quotation-pay-link', {
      body: { customer_profile_id: customerProfileId },
    });
    if (error) throw new Error(await extractError(error));
    return data as QuotationPayLink;
  },

  async batchAssign(leadIds: string[], agentId: string): Promise<{ assigned: number; rejected: Array<{ lead_id: string; reason: string }> }> {
    const { data, error } = await supabase.functions.invoke('lead-batch-assign', {
      headers: { 'Idempotency-Key': uuid() },
      body: { lead_ids: leadIds, agent_id: agentId },
    });
    if (error) throw new Error(await extractError(error));
    return data as { assigned: number; rejected: Array<{ lead_id: string; reason: string }> };
  },

  async batchImport(leads: unknown[], certification: true, originatingCompanyId?: string): Promise<{ accepted: number; skipped_duplicate: number; errored: number; rows: Array<{ row_index: number; status: string; customer_id?: string; reason?: string }> }> {
    const { data, error } = await supabase.functions.invoke('lead-batch-import', {
      headers: { 'Idempotency-Key': uuid() },
      body: { leads, certification, originating_company_id: originatingCompanyId },
    });
    if (error) throw new Error(await extractError(error));
    return data as { accepted: number; skipped_duplicate: number; errored: number; rows: Array<{ row_index: number; status: string; customer_id?: string; reason?: string }> };
  },

  /** Single-lead create — agent/manager/admin. Agent restricted to walk_in/inbound_call/referral lead_source. */
  async createLead(input: {
    full_name: string;
    email?: string;
    phone?: string;
    company_name?: string;
    lead_source: LeadSource;
    consent_basis: 'opt_in_form' | 'existing_customer' | 'referral' | 'walk_in' | 'legitimate_interest';
    consent_source: string;
    consent_at: string;
    initial_note?: string;
    certification: true;
    originating_company_id?: string;
    assigned_agent_id?: string;
  }): Promise<{ ok: true; status: 'accepted' | 'duplicate'; customer_id: string; reason?: string }> {
    const { data, error } = await supabase.functions.invoke('lead-create', {
      headers: { 'Idempotency-Key': uuid() },
      body: input,
    });
    if (error) throw new Error(await extractError(error));
    return data as { ok: true; status: 'accepted' | 'duplicate'; customer_id: string; reason?: string };
  },

  async inviteStaff(email: string, displayName: string, role: 'sales_agent' | 'sales_manager', companyId?: string): Promise<{ user_id: string }> {
    const { data, error } = await supabase.functions.invoke('staff-invite', {
      headers: { 'Idempotency-Key': uuid() },
      body: { email, display_name: displayName, role, company_id: companyId },
    });
    if (error) throw new Error(await extractError(error));
    return data as { user_id: string };
  },

  async deactivateStaff(userId: string, reassignToAgentId: string): Promise<{ leads_reassigned: number }> {
    const { data, error } = await supabase.functions.invoke('staff-deactivate', {
      headers: { 'Idempotency-Key': uuid() },
      body: { user_id: userId, reassign_to_agent_id: reassignToAgentId },
    });
    if (error) throw new Error(await extractError(error));
    return data as { leads_reassigned: number };
  },
};

/**
 * Pull the Edge Function's OWN error message out of a supabase-js failure.
 *
 * This used to return `e.message` only, which for any non-2xx is the generic
 * supabase-js string "Edge Function returned a non-2xx status code". Every
 * useful message our EFs send was being discarded at this line.
 *
 * That is not cosmetic. On 2026-07-29 agents could not create Basic or Super
 * customers because createHostingOrder returned
 *   503 reseller_at_cap — "We can't allocate the disk this plan needs right now."
 * The agent saw only the generic string, so it reached the owner as "an issue
 * with loading customers new" and cost a full investigation to identify a
 * condition the server had already named precisely.
 *
 * supabase-js has shipped two shapes for `context` across versions — a fetch
 * Response (current) and a `{ body: string }` (older). Both are handled; the
 * Response is cloned so the caller can still read it.
 */
async function extractError(err: unknown): Promise<string> {
  const e = err as { message?: string; context?: unknown };
  const ctx = e.context;

  // Current shape: context IS the Response.
  if (ctx && typeof (ctx as Response).clone === 'function') {
    try {
      const raw = await (ctx as Response).clone().text();
      const parsed = JSON.parse(raw) as { message?: string; code?: string };
      if (parsed?.message) return parsed.message;
      if (parsed?.code) return parsed.code;
    } catch {
      /* not JSON, or already consumed — fall through */
    }
  }

  // Legacy shape: context.body is the raw string.
  const legacyBody = (ctx as { body?: unknown } | undefined)?.body;
  if (typeof legacyBody === 'string') {
    try {
      const parsed = JSON.parse(legacyBody) as { message?: string; code?: string };
      if (parsed?.message) return parsed.message;
      if (parsed?.code) return parsed.code;
    } catch {
      /* fall through */
    }
  }

  return e.message ?? 'Edge function call failed';
}
